/**
 * Play Now: a hostless game anyone can start without an account. Four phases in one view, on one
 * socket — set up the game, wait in a lobby while players scan the code, play inline, then see the
 * final score. Keeping
 * it on one socket matters for solo play: navigating away would disconnect the only player and
 * stall their pick.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Snapshot } from '@gsf/contract';
import { api, type CreatedPlayNow, type PlayNowCategory, type PlayNowRequest } from '../lib/api';
import { useSession, type JoinParams } from '../lib/useSession';
import { useRecordResult } from '../lib/results';
import { AccountNudge } from '../components/AccountNudge';
import { GameReview } from '../components/GameReview';
import { CopyLink, JoinedPlayers } from '../components/Lobby';
import { clearPlayer, loadPlayer, savePlayer } from '../lib/storage';
import { notePlayerChanged } from '../lib/whoami';
import { uiFor } from '../games';
import { JoinQr } from '../kit/JoinQr';
import { Toast } from '../kit/Toast';
import { Interstitial } from '../show/Interstitial';
import { LeaderBadge } from '../show/LeaderBadge';
import { StartCountdown } from '../show/StartCountdown';
import { useOutgrowBoard } from '../workshop/frame';

type Template = PlayNowRequest['template'];
type AnswerMode = 'highScore' | 'buzzer';

/** Picks a player may make; the server clamps to the same number (`MAX_PICKED_CATEGORIES`). */
const MAX_CATEGORIES = 3;

/** What carries into a rematch: the new game, who you are, and the settings it was built from. */
interface Rematch {
  readonly created: CreatedPlayNow;
  readonly name: string;
  readonly solo: boolean;
  readonly body: PlayNowRequest;
}

export function PlayNow() {
  // Keyed by code: a rematch remounts with a fresh socket, so it leaves the old game's room.
  const [rematch, setRematch] = useState<Rematch | null>(null);
  return <PlayNowGame key={rematch?.created.code ?? ''} rematch={rematch} onRematch={setRematch} />;
}

function PlayNowGame({ rematch, onRematch }: { rematch: Rematch | null; onRematch(next: Rematch): void }) {
  const [categories, setCategories] = useState<PlayNowCategory[]>([]);
  /** The "Other formats" pick; the main button is always a Quick Game. */
  const [template, setTemplate] = useState<Exclude<Template, 'quick'>>('board');
  const [answerMode, setAnswerMode] = useState<AnswerMode>('buzzer');
  /** Null until the creator touches the box; then their choice wins over the mode default. */
  const [losePointsSet, setLosePoints] = useState<boolean | null>(null);
  const [speedBonus, setSpeedBonus] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  /** Set by "Random": the next pick replaces the rolled category instead of joining it. */
  const [onlyChosen, setOnlyChosen] = useState(false);
  const [name, setName] = useState(rematch?.name ?? '');
  /** Solo starts the moment the game is built; multiplayer waits in the lobby for others to join. */
  const [solo, setSolo] = useState(rematch?.solo ?? true);
  /** What built this game, so Play Again builds the next the same way. */
  const [built, setBuilt] = useState<PlayNowRequest | null>(rematch?.body ?? null);

  const [created, setCreated] = useState<CreatedPlayNow | null>(rematch?.created ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Once seated, every reconnect resumes this seat by token; replaying the first join (a name,
  // no token) would seat a stranger and freeze this screen while the game plays on.
  const [rejoin, setRejoin] = useState<JoinParams | undefined>();
  const { snapshot, connected, joined, join, submit, send, events, toast, notify, clearToast } =
    useSession(rejoin);
  useRecordResult(snapshot);

  // Setup fits on the workshop's board; the lobby and the game take the whole screen.
  useOutgrowBoard(created !== null);

  useEffect(() => {
    void api
      .getPlayNowCategories()
      .then((r) => setCategories(r.categories))
      .catch(() => {
        /* the picker just shows "random mix" with no list */
      });
  }, []);

  // Pub trivia is high-score only; the board offers both modes.
  const effectiveMode: AnswerMode = template === 'pub' ? 'highScore' : answerMode;

  // Lose points defaults ON for buzzer, OFF for high score, but never overrides a manual choice (spec).
  const losePoints = losePointsSet ?? effectiveMode === 'buzzer';

  async function create(event: React.FormEvent, format: Template) {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      // A quick game's scoring is fixed server-side; the format toggles only shape the others.
      const body: PlayNowRequest = {
        template: format,
        answerMode: effectiveMode,
        losePoints,
        speedBonus,
        ...(chosen.size > 0 ? { categories: [...chosen] } : {}),
      };
      const res = await api.createPlayNow(body);
      setBuilt(body);
      await takeSeat(res, name.trim(), solo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the game.');
    } finally {
      setBusy(false);
    }
  }

  /** Join as a player so the creator plays, and so a solo game has someone to pick first. */
  async function takeSeat(res: CreatedPlayNow, playerName: string, startNow: boolean) {
    const ack = await join({ as: 'player', code: res.code, name: playerName });
    if (ack.ok && ack.token) {
      savePlayer(res.code, { token: ack.token, name: playerName });
      setRejoin({ as: 'player', code: res.code, name: playerName, token: ack.token });
      notePlayerChanged();
    }
    setCreated(res);
    // A failed solo start falls back to the lobby (its error and Start button) via `error`.
    if (startNow && ack.ok) await api.startPlayNow(res.code, res.creatorToken);
  }

  // A rematch arrives built; take this seat in it once (a ref, so StrictMode's re-run cannot seat twice).
  const seated = useRef(false);
  useEffect(() => {
    if (!rematch || seated.current) return;
    seated.current = true;
    setBusy(true);
    takeSeat(rematch.created, rematch.name, rematch.solo)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not join the new game.'))
      .finally(() => setBusy(false));
    // Runs once per mount; this component is keyed by the game's code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Build the next game from this one's settings; the server moves this game's players to it. */
  async function playAgain(picked: string[]) {
    if (!created || !built) return;
    const { categories: _previous, ...settings } = built;
    const body: PlayNowRequest = { ...settings, ...(picked.length > 0 ? { categories: picked } : {}) };
    const res = await api.createPlayNow({
      ...body,
      rematchOf: { code: created.code, creatorToken: created.creatorToken },
    });
    onRematch({ created: res, name: name.trim(), solo, body });
  }

  async function start() {
    if (!created) return;
    setBusy(true);
    setError(null);
    try {
      await api.startPlayNow(created.code, created.creatorToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the game.');
    } finally {
      setBusy(false);
    }
  }

  const onLeave = useCallback(() => {
    if (created) clearPlayer(created.code);
    notePlayerChanged();
    location.assign('/');
  }, [created]);

  // ---- Phase 4: the show is over — this player's score ----
  const stage = snapshot?.core.stage;
  if (created && snapshot && stage === 'results') {
    return (
      <FinalScore
        snapshot={snapshot}
        code={created.code}
        token={loadPlayer(created.code)?.token ?? null}
        onLeave={onLeave}
        playAgain={built ? { categories, picked: built.categories ?? [], onPlay: playAgain } : undefined}
      />
    );
  }

  // ---- Phase 3: the game is running — play inline, exactly as the player view would ----
  if (created && snapshot && stage && stage !== 'lobby') {
    const segment = snapshot.core.segment;
    if (!segment) {
      return (
        <>
          <Interstitial snapshot={snapshot} send={send} surface="player" connected={connected} />
          <Toast message={toast} onDismiss={clearToast} />
        </>
      );
    }
    const ui = uiFor(segment.gameId);
    return (
      <>
        <LeaderBadge snapshot={snapshot} />
        {ui ? (
          <ui.player
            snapshot={snapshot}
            send={send}
            submit={submit}
            events={events}
            connected={connected}
            notify={notify}
            onLeave={onLeave}
          />
        ) : (
          <div className="centered">
            <p className="muted">Starting…</p>
          </div>
        )}
        <StartCountdown snapshot={snapshot} />
        <Toast message={toast} onDismiss={clearToast} />
      </>
    );
  }

  // ---- Solo: no lobby, just the beat before the first question arrives ----
  if (created && solo && !error) {
    return (
      <div className="centered">
        <p className="muted">Starting…</p>
      </div>
    );
  }

  // ---- Phase 2: the lobby — share the code, then start ----
  if (created) {
    return (
      <div className="centered">
        <div className="col" style={{ width: 'min(34rem, 100%)', gap: '0.75rem' }}>
          <div className="card col">
            <h1>Game ready</h1>
            <p className="muted" style={{ marginTop: 0 }}>
              Everyone opens the link or scans the code to join. Press start when you’re all in.
            </p>
            <JoinQr code={created.code} />
            <CopyLink url={created.joinUrl} />
            {snapshot ? <JoinedPlayers snapshot={snapshot} /> : null}
            {created.gaps > 0 ? (
              <p className="faint">
                {created.clues} clues ready. {created.gaps} tile{created.gaps === 1 ? '' : 's'}{' '}
                couldn’t be filled from the bank and were left off the board.
              </p>
            ) : null}
            {error ? <p className="error-text">{error}</p> : null}
            <button
              className="btn btn-primary btn-lg"
              onClick={start}
              disabled={busy || !connected || !joined}
            >
              {joined ? (busy ? 'Starting…' : 'Start game') : 'Joining…'}
            </button>
            <Link className="btn btn-ghost" to={`/board/${created.code}`} target="_blank" rel="noreferrer">
              Open the board on a TV
            </Link>
          </div>
          <Link className="btn btn-ghost" to="/">
            ← Leave
          </Link>
        </div>
      </div>
    );
  }

  // ---- Phase 1: setup ----
  return (
    <div className="centered">
      <div className="col" style={{ width: 'min(38rem, 100%)', gap: '0.75rem' }}>
        {/* Enter in the name field starts a Quick Game; the other formats have their own button. */}
        <form className="card col" style={{ width: '100%' }} onSubmit={(e) => create(e, 'quick')}>
          <h1>Play Now</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            Ten questions, 15 seconds each. 100 points for a right answer, plus up to 50 more for a
            fast one. No host needed.
          </p>

          <div className="field">
            <label htmlFor="pn-name">Your name</label>
            <input
              id="pn-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={24}
              autoComplete="given-name"
              placeholder="e.g. Andrew"
            />
          </div>

          <div className="field">
            <label id="pn-players">Players</label>
            <div className="row" role="radiogroup" aria-labelledby="pn-players" style={{ gap: '0.4rem' }}>
              <label className="game-choice" style={{ flex: 1 }}>
                <input type="radio" name="players" checked={solo} onChange={() => setSolo(true)} />
                <strong>Solo</strong>
              </label>
              <label className="game-choice" style={{ flex: 1 }}>
                <input type="radio" name="players" checked={!solo} onChange={() => setSolo(false)} />
                <strong>Multiplayer</strong>
              </label>
            </div>
          </div>

          {categories.length > 0 ? (
            <CategoryPicker
              categories={categories}
              chosen={chosen}
              only={onlyChosen}
              onChange={(next, only) => {
                setChosen(next);
                setOnlyChosen(only);
              }}
            />
          ) : null}

          {error ? <p className="error-text">{error}</p> : null}

          <button className="btn btn-primary btn-lg" type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Building…' : 'Quick Game'}
          </button>

          <details className="pn-more">
            <summary>Other formats</summary>
            <div className="col" style={{ gap: '0.75rem', marginTop: '0.75rem' }}>
              <div className="field">
                <label>Template</label>
                <div className="col" style={{ gap: '0.4rem' }}>
                  <label className="game-choice">
                    <input
                      type="radio"
                      name="template"
                      checked={template === 'board'}
                      onChange={() => setTemplate('board')}
                    />
                    <span className="col" style={{ gap: 0, minWidth: 0 }}>
                      <strong>Trivia board</strong>
                      <span className="faint">
                        Two rounds of five categories; the second doubles the values.
                      </span>
                    </span>
                  </label>
                  <label className="game-choice">
                    <input
                      type="radio"
                      name="template"
                      checked={template === 'pub'}
                      onChange={() => setTemplate('pub')}
                    />
                    <span className="col" style={{ gap: 0, minWidth: 0 }}>
                      <strong>Pub quiz</strong>
                      <span className="faint">Six rounds of five questions.</span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="field">
                <label>Answer mode</label>
                <div className="col" style={{ gap: '0.4rem' }}>
                  <label className="game-choice">
                    <input
                      type="radio"
                      name="answerMode"
                      checked={answerMode === 'buzzer'}
                      onChange={() => setAnswerMode('buzzer')}
                    />
                    <span className="col" style={{ gap: 0, minWidth: 0 }}>
                      <strong>Quickest buzzer</strong>
                      <span className="faint">First to buzz answers alone; the winner picks next.</span>
                    </span>
                  </label>
                  <label className="game-choice">
                    <input
                      type="radio"
                      name="answerMode"
                      checked={answerMode === 'highScore'}
                      onChange={() => setAnswerMode('highScore')}
                    />
                    <span className="col" style={{ gap: 0, minWidth: 0 }}>
                      <strong>High score</strong>
                      <span className="faint">Everyone answers each clue; take turns picking.</span>
                    </span>
                  </label>
                </div>
              </div>

              <label className="field-check">
                <input type="checkbox" checked={losePoints} onChange={(e) => setLosePoints(e.target.checked)} />
                <span>Lose points for a wrong answer</span>
              </label>
              <label className="field-check">
                <input type="checkbox" checked={speedBonus} onChange={(e) => setSpeedBonus(e.target.checked)} />
                <span>Bonus points for a fast answer</span>
              </label>

              <button
                className="btn"
                type="button"
                disabled={busy || !name.trim()}
                onClick={(e) => create(e, template)}
              >
                {busy ? 'Building…' : `Create ${template === 'pub' ? 'pub quiz' : 'trivia board'}`}
              </button>
            </div>
          </details>
        </form>

        <Link className="btn btn-ghost" to="/">
          ← Back
        </Link>
      </div>
    </div>
  );
}

/** Names around the drum: 12 faces at 30° apart reads as a cylinder without crowding. */
const DRUM_FACES = 12;
/** Full turns before it settles, and how long the spin takes (matches `.pn-drum-ring`'s animation). */
const DRUM_TURNS = 3;
const DRUM_MS = 2600;

interface Drum {
  readonly faces: readonly string[];
  /** The ring's final rotation: whole turns plus the landing face's angle. */
  readonly endAngle: number;
}

/**
 * Pick up to `MAX_CATEGORIES` without listing them all: search (a handful of matches), browse (a
 * native dropdown), or roll one at random and play only that. Nothing chosen is a random mix.
 */
function CategoryPicker({
  categories,
  chosen,
  only,
  onChange,
}: {
  categories: PlayNowCategory[];
  chosen: Set<string>;
  only: boolean;
  onChange(next: Set<string>, only: boolean): void;
}) {
  const [query, setQuery] = useState('');
  /** The spinning drum; null at rest. */
  const [drum, setDrum] = useState<Drum | null>(null);
  const timer = useRef<number | undefined>(undefined);
  /** The current spin's landing, for `animationend` to call. */
  const landRef = useRef<() => void>(() => undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // A rolled category is replaced by the next pick, so only a hand-picked set can be full.
  const full = chosen.size >= MAX_CATEGORIES && !only;
  // Too many tags to list; a search shows a handful of matches the player hasn't picked yet.
  const needle = query.trim().toLowerCase();
  const matches = needle
    ? categories.filter((c) => !chosen.has(c.tag) && c.tag.toLowerCase().includes(needle)).slice(0, 8)
    : [];
  const browse = useMemo(() => [...categories].sort((a, b) => a.tag.localeCompare(b.tag)), [categories]);
  const rolled = only ? ([...chosen][0] ?? '') : '';

  // A hand-picked category always mixes: it ends a single-category roll.
  function add(tag: string) {
    if (!tag || chosen.has(tag) || full) return;
    onChange(only ? new Set([tag]) : new Set([...chosen, tag]), false);
    setQuery('');
  }
  function remove(tag: string) {
    const next = new Set(chosen);
    next.delete(tag);
    onChange(next, false);
  }

  function roll() {
    if (categories.length === 0 || drum !== null) return;
    // Up to twelve distinct names round the drum, repeating when fewer are offered. Every offered
    // category has at least 20 questions (`MIN_OFFERED_QUESTIONS`), enough for a Quick Game alone.
    const pool = [...categories].sort(() => Math.random() - 0.5);
    const faces = Array.from({ length: DRUM_FACES }, (_unused, i) => pool[i % pool.length]!.tag);
    const landing = Math.floor(Math.random() * DRUM_FACES);
    const land = () => {
      window.clearTimeout(timer.current);
      setDrum(null);
      onChange(new Set([faces[landing]!]), true);
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      land();
      return;
    }
    setDrum({ faces, endAngle: DRUM_TURNS * 360 + (landing * 360) / DRUM_FACES });
    // `animationend` lands it; this covers a tab hidden mid-spin, where it may never fire.
    timer.current = window.setTimeout(land, DRUM_MS + 400);
    landRef.current = land;
  }

  const status = drum
    ? 'spinning…'
    : only
      ? `only ${rolled}`
      : chosen.size === 0
        ? 'Leave Blank for a Random Mix'
        : `${chosen.size} of ${MAX_CATEGORIES}`;

  return (
    <div className="field">
      <label htmlFor="pn-category">Categories ({status})</label>
      {drum !== null ? (
        <div className="pn-drum" aria-hidden="true">
          <div
            className="pn-drum-ring"
            style={{ '--drum-end': `${String(drum.endAngle)}deg` } as React.CSSProperties}
            onAnimationEnd={(e) => {
              if (e.target === e.currentTarget) landRef.current();
            }}
          >
            {drum.faces.map((tag, i) => (
              <span
                key={i}
                className="pn-drum-face"
                style={{ transform: `rotateX(${String((-i * 360) / DRUM_FACES)}deg) translateZ(var(--drum-r))` }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      ) : chosen.size > 0 ? (
        <div className="row" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
          {[...chosen].map((tag) => (
            <button
              type="button"
              key={tag}
              className={`btn btn-sm btn-primary${only ? ' pn-landed' : ''}`}
              aria-label={`Remove ${tag}`}
              onClick={() => remove(tag)}
            >
              {tag} ×
            </button>
          ))}
        </div>
      ) : null}
      {/* Announces where the roll landed, not every name it spun past. */}
      <span className="sr-only" aria-live="polite">
        {only && drum === null ? `Random category: ${rolled}` : ''}
      </span>
      <input
        id="pn-category"
        type="search"
        autoComplete="off"
        placeholder={full ? `Up to ${MAX_CATEGORIES} categories` : 'Search…'}
        disabled={full}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          // Enter adds the top match instead of submitting the setup form.
          if (e.key !== 'Enter') return;
          e.preventDefault();
          if (matches[0]) add(matches[0].tag);
        }}
      />
      {matches.length > 0 ? (
        <div className="row" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
          {matches.map((c) => (
            <button type="button" key={c.tag} className="btn btn-sm" onClick={() => add(c.tag)}>
              {c.tag}
            </button>
          ))}
        </div>
      ) : needle ? (
        <span className="faint">No category matches “{query.trim()}”.</span>
      ) : null}
      <div className="row" style={{ gap: '0.4rem' }}>
        <select
          aria-label="Browse all categories"
          value=""
          disabled={full}
          onChange={(e) => add(e.target.value)}
          style={{ flex: 1, minWidth: 0 }}
        >
          <option value="">Browse all…</option>
          {browse
            .filter((c) => !chosen.has(c.tag))
            .map((c) => (
              <option key={c.tag} value={c.tag}>
                {c.tag}
              </option>
            ))}
        </select>
        <button type="button" className="btn btn-sm" onClick={roll} disabled={drum !== null}>
          {only ? 'Spin again' : 'Random'}
        </button>
      </div>
    </div>
  );
}

/** The creator's Play Again: pick categories (last game's to start), then build the rematch. */
interface PlayAgainProps {
  categories: PlayNowCategory[];
  picked: readonly string[];
  onPlay(categories: string[]): Promise<void>;
}

function PlayAgain({ categories, picked, onPlay }: PlayAgainProps) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(picked));
  const [only, setOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    setBusy(true);
    setError(null);
    try {
      await onPlay([...chosen]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not build the next game.');
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button className="btn btn-primary btn-lg" type="button" onClick={() => setOpen(true)}>
        Play Again
      </button>
    );
  }
  return (
    <div className="card col">
      <h2 style={{ margin: 0 }}>Play Again</h2>
      <p className="faint" style={{ margin: 0 }}>
        Everyone here moves to the new game’s lobby. You start it when they’re in.
      </p>
      {categories.length > 0 ? (
        <CategoryPicker
          categories={categories}
          chosen={chosen}
          only={only}
          onChange={(next, rolled) => {
            setChosen(next);
            setOnly(rolled);
          }}
        />
      ) : null}
      {error ? <p className="error-text">{error}</p> : null}
      <button className="btn btn-primary btn-lg" type="button" onClick={go} disabled={busy}>
        {busy ? 'Building…' : 'Build next game'}
      </button>
    </div>
  );
}

/** The end of a Play Now game: this player's score, large, then the table. */
function FinalScore({
  snapshot,
  code,
  token,
  onLeave,
  playAgain,
}: {
  snapshot: Snapshot;
  code: string;
  token: string | null;
  onLeave(): void;
  /** The creator's only. */
  playAgain?: PlayAgainProps | undefined;
}) {
  const standings = snapshot.core.show.standings;
  const me = standings.find((s) => s.target.id === snapshot.you.participantId);

  return (
    <div className="centered">
      <div className="card col pn-final" style={{ width: 'min(34rem, 100%)' }}>
        <h1>{snapshot.core.title} — final score</h1>
        {me ? (
          <p className="pn-final-score" aria-label={`Your score: ${me.showTotal}`}>
            {me.showTotal}
          </p>
        ) : null}
        {standings.length > 1 ? (
          <ol className="show-standings">
            {standings.map((s) => (
              <li key={s.target.id}>
                <span className="show-rank">{s.rank}</span>
                <span className="show-name">{s.displayName}</span>
                <span className="lb-score">{s.showTotal}</span>
              </li>
            ))}
          </ol>
        ) : null}
        <GameReview code={code} token={token} />
        <Link className="btn btn-ghost" to="/leaderboard">
          Leaderboard
        </Link>
        {playAgain ? <PlayAgain {...playAgain} /> : null}
        <button className={playAgain ? 'btn' : 'btn btn-primary btn-lg'} onClick={onLeave}>
          Done
        </button>
        <AccountNudge />
      </div>
    </div>
  );
}
